import { currentStoreView } from '@vue-storefront/core/lib/multistore'

/** dayjs `localizedFormat` presets → `Intl.DateTimeFormat` options */
const LOCALIZED_PRESETS = {
  L: { dateStyle: 'short' },
  LL: { dateStyle: 'medium' },
  LLL: { dateStyle: 'medium', timeStyle: 'short' },
  LLLL: {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }
}

/** dayjs-style tokens (longest alternation first). Case-sensitive: M = month, m = minute */
const DAYJS_LIKE_TOKEN = /(YYYY|MMMM|MMM|YY|MM|DD|HH|hh|mm|ss|A|M|D|H|h|m|s)/g

function parseDateInput (value) {
  if (value === null || value === undefined || value === '') return null
  const d = value instanceof Date ? new Date(value.getTime()) : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatWithIntlPreset (d, preset, locale) {
  try {
    return new Intl.DateTimeFormat(locale, preset).format(d)
  } catch (e) {
    return new Intl.DateTimeFormat(undefined, preset).format(d)
  }
}

function formatDayjsLikePattern (d, pattern, locale) {
  const monthPart = (style) => new Intl.DateTimeFormat(locale, { month: style }).format(d)
  const pad = (n, w = 2) => String(Math.floor(n)).padStart(w, '0')
  const H24 = d.getHours()
  const h12 = H24 % 12 || 12
  const formatters = {
    YYYY: () => String(d.getFullYear()),
    YY: () => String(d.getFullYear()).slice(-2),
    MMMM: () => monthPart('long'),
    MMM: () => monthPart('short'),
    MM: () => pad(d.getMonth() + 1),
    M: () => String(d.getMonth() + 1),
    DD: () => pad(d.getDate()),
    D: () => String(d.getDate()),
    HH: () => pad(H24),
    H: () => String(H24),
    hh: () => pad(h12),
    h: () => String(h12),
    mm: () => pad(d.getMinutes()),
    m: () => String(d.getMinutes()),
    ss: () => pad(d.getSeconds()),
    s: () => String(d.getSeconds()),
    A: () => (H24 >= 12 ? 'PM' : 'AM')
  }
  return pattern.replace(DAYJS_LIKE_TOKEN, (match) => {
    const fn = formatters[match]
    return fn ? fn() : match
  })
}

/**
 * Converts date to format provided as an argument or defined in config file (if argument not provided)
 * @param {String|Date|Number} date
 * @param {String} format
 */
export function date (dateInput, format, storeView) {
  const _storeView = storeView || currentStoreView()
  const displayFormat = format || _storeView.i18n.dateFormat
  const storeLocale = _storeView.i18n.defaultLocale

  const d = parseDateInput(dateInput)
  if (!d) return ''

  const preset = LOCALIZED_PRESETS[displayFormat]
  if (preset) {
    return formatWithIntlPreset(d, preset, storeLocale)
  }

  try {
    return formatDayjsLikePattern(d, displayFormat, storeLocale)
  } catch (e) {
    return formatDayjsLikePattern(d, displayFormat, 'en-US')
  }
}
